import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const IdealClients = () => {
  const clients = [
    {
      title: 'Founders',
      description: 'Building companies that have hit a growth ceiling',
      icon: '👤',
      criteria: [
        'Revenue between $1M-$50M',
        'Clear product-market fit',
        'Growth has plateaued',
        'Ready to scale',
      ],
    },
    {
      title: 'CEOs',
      description: 'Leading teams through inflection points',
      icon: '🎯',
      criteria: [
        'Facing market repositioning',
        'Navigating category creation',
        'Managing narrative shift',
        'Preparing for fundraise',
      ],
    },
    {
      title: 'Executives',
      description: 'Driving strategic transformation',
      icon: '⚡',
      criteria: [
        'CMO or VP Marketing',
        'Head of Product',
        'Chief Strategy Officer',
        'Seeking external perspective',
      ],
    },
  ];

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden bg-[#080808]">
      {/* Ambient glow - desktop only */}
      <div className="hidden lg:block absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full bg-accent-blue blur-[160px] opacity-10" />
      </div>

      {/* Minimal static glow for mobile */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[200px] h-[200px] rounded-full bg-accent-blue blur-[70px] opacity-8" />
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="text-[32px] lg:text-5xl font-bold text-white mb-4 lg:mb-5 leading-tight">
            Who We Work With
          </h2>
          <p className="text-[15px] lg:text-lg text-gray-400 max-w-[650px] mx-auto leading-relaxed">
            We partner with leaders who recognize that strategic clarity is the bottleneck
          </p>
        </motion.div>

        {/* DESKTOP VERSION - Animated cards */}
        <div className="hidden lg:grid grid-cols-3 gap-8 mb-16">
          {clients.map((client, index) => (
            <motion.div
              key={client.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="group"
            >
              <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0e13] border border-white/10 rounded-2xl p-8 h-full relative overflow-hidden hover:border-accent-blue/40 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="text-5xl mb-5">{client.icon}</div>
                  
                  <h3 className="text-2xl font-bold text-white mb-3">{client.title}</h3>
                  <p className="text-sm text-gray-400 mb-6 leading-relaxed">{client.description}</p>
                  
                  <ul className="space-y-3">
                    {client.criteria.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-gray-300">
                        <i className="ri-check-line text-accent-blue text-lg mt-0.5 flex-shrink-0" />
                        <span className="text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* MOBILE VERSION - Static stacked cards, instant load */}
        <div className="lg:hidden space-y-5 mb-10">
          {clients.map((client, index) => (
            <div
              key={client.title}
              className="opacity-0 animate-[fadeIn_0.4s_ease-out_forwards]"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="bg-gradient-to-br from-[#0f1419] to-[#0a0e13] border border-white/10 rounded-xl p-6 relative overflow-hidden active:border-accent-blue/40 transition-colors duration-200">
                <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-50" />
                
                <div className="relative z-10">
                  <div className="text-4xl mb-4">{client.icon}</div>
                  
                  <h3 className="text-xl font-bold text-white mb-2">{client.title}</h3>
                  <p className="text-[13px] text-gray-400 mb-5 leading-relaxed">{client.description}</p>
                  
                  <ul className="space-y-2.5">
                    {client.criteria.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-gray-300">
                        <i className="ri-check-line text-accent-blue text-base mt-0.5 flex-shrink-0" />
                        <span className="text-[13px] leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA - optimized for both desktop and mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <Link
            to="/apply"
            className="inline-block px-6 lg:px-8 py-3.5 lg:py-4 bg-accent-blue text-white text-[15px] lg:text-base font-semibold rounded-full hover:brightness-125 lg:hover:-translate-y-1 lg:hover:shadow-[0_0_40px_rgba(0,102,255,0.5)] active:scale-95 lg:active:scale-100 transition-all duration-200 lg:duration-300 whitespace-nowrap cursor-pointer"
          >
            Apply for a Bottleneck Audit
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default IdealClients;